import { apiClient } from "../../lib/http/apiClient";
import { adminHeaders } from "../admin/service";

/** One structured "what changed" entry — mirrors `ChangeItem` in
 * backend/app/modules/legal/schemas.py. Admin-authored: `diffDocuments`
 * below can PRE-FILL this from an HTML diff, but nothing publishes it to an
 * end user without the admin having seen and (optionally) edited it in the
 * form first — see backend/app/modules/legal/diffing.py. */
export interface LegalChange {
  section: string;
  summary: string;
}

/** One word-level highlight segment inside a changed paragraph — mirrors
 * the backend's `word_diff` entries. */
export interface LegalDiffWordSegment {
  type: "equal" | "added" | "removed";
  text: string;
}

/** One row of the admin-only "Compare versions" view — mirrors `DiffRow`.
 * Never shown to end users. */
export interface LegalDiffRow {
  type: "unchanged" | "added" | "removed" | "changed";
  section: string;
  old_text?: string | null;
  new_text?: string | null;
  word_diff?: LegalDiffWordSegment[] | null;
}

/** Mirrors `LegalDocumentDiffOut`. `truncated` is true when the real change
 * count was collapsed into a single "substantial rewrite" note (see
 * diffing.py) — `changes` is a pre-fill; `rows` is always the full,
 * uncollapsed compare for the viewer. */
export interface LegalDocumentDiff {
  changes: LegalChange[];
  truncated: boolean;
  rows: LegalDiffRow[];
}

/** A versioned legal document (T&C, privacy policy, …) as the admin sees it —
 * mirrors `LegalDocumentOut` in backend/app/modules/legal/schemas.py, which
 * adds the lifecycle/audit fields on top of the public payload. */
export interface LegalDocument {
  id: string;
  type: string;
  version: string;
  title: string;
  content?: string | null;
  content_url?: string | null;
  status: LegalDocumentStatus;
  effective_at?: string | null;
  /** Null = no grace period; enforcement is immediate once published. */
  reacceptance_deadline?: string | null;
  published_at?: string | null;
  requires_reacceptance: boolean;
  change_summary?: string | null;
  changes?: LegalChange[] | null;
  locale?: string | null;
  country?: string | null;
  created_by?: string | null;
  published_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export type LegalDocumentStatus = "draft" | "published" | "archived";

/** Create payload — always produces a DRAFT; publishing is a separate action. */
export interface LegalDocumentCreate {
  type: string;
  version: string;
  title: string;
  content: string;
  content_url?: string | null;
  effective_at?: string | null;
  reacceptance_deadline?: string | null;
  requires_reacceptance: boolean;
  change_summary?: string | null;
  changes?: LegalChange[] | null;
  locale?: string | null;
  country?: string | null;
}

/** Draft-only edit payload. `type`/`version` are fixed at creation and a
 * published version is immutable — cut a new version instead. */
export type LegalDocumentUpdate = Omit<Partial<LegalDocumentCreate>, "type" | "version">;

export interface LegalDocumentListParams {
  type?: string;
  status?: LegalDocumentStatus | "";
  limit?: number;
  skip?: number;
}

/** One admin acceptance-table row — mirrors `AdminAcceptanceRow`. Carries user
 * identity (email/name) via a server-side join; `status="pending"` rows are
 * an active user with no acceptance record, so their audit fields are null. */
export interface LegalAcceptance {
  user_id: string;
  email?: string | null;
  name?: string | null;
  status: "accepted" | "pending";
  document_version?: string | null;
  accepted_at?: string | null;
  ip_address?: string | null;
  platform?: string | null;
  locale?: string | null;
  country?: string | null;
}

export interface LegalAcceptanceListParams {
  /** "accepted" (default) or "pending". Platform/country/locale/date filters
   * only apply to "accepted" — a pending user has no acceptance row to filter on. */
  status?: "accepted" | "pending";
  platform?: string;
  country?: string;
  locale?: string;
  acceptedFrom?: string;
  acceptedTo?: string;
  /** Matches the joined user's email or name, server-side. */
  search?: string;
  limit?: number;
  skip?: number;
}

/** Mirrors `AdminAcceptanceListOut` — server-side filtered + paginated. */
export interface LegalAcceptanceListResult {
  items: LegalAcceptance[];
  total: number;
  limit: number;
  skip: number;
  status: "accepted" | "pending";
}

/** Mirrors `AcceptanceReportOut`. `acceptance_rate` is a fraction 0–1. */
export interface LegalAcceptanceReport {
  document_id: string;
  document_type: string;
  document_version: string;
  status: string;
  total_users: number;
  accepted_count: number;
  pending_count: number;
  acceptance_rate: number;
}

/** One enforcement point — an action the backend blocks until the user has
 * accepted the current legal documents. The list is hard-coded server-side
 * (not a setting), so this is read-only in the admin UI. */
export interface GatedAction {
  action: string;
  method: string;
  path: string;
  description: string;
}

/** One acceptance row in a single user's cross-document history. Unlike
 * `LegalAcceptance` these are always real acceptance records (no "pending"
 * rows), so they carry the document they were recorded against. */
export interface UserAcceptanceHistoryEntry {
  id: string;
  legal_document_id: string;
  document_type: string;
  document_version: string;
  accepted_at?: string | null;
  ip_address?: string | null;
  platform?: string | null;
  locale?: string | null;
  country?: string | null;
}

/** One currently-published document this user has NOT yet accepted — same
 * shape as the public document payload, metadata only (no content — this is
 * an admin status list, not a reader). */
export interface UserPendingDocument {
  id: string;
  type: string;
  version: string;
  title: string;
  requires_reacceptance: boolean;
  change_summary?: string | null;
}

/** Every acceptance a single user has made, across all documents, PLUS what
 * they currently still owe — both scoped to this one user only, never a
 * global accepted/pending tally. */
export interface UserAcceptanceHistory {
  user_id: string;
  email?: string | null;
  name?: string | null;
  acceptances: UserAcceptanceHistoryEntry[];
  pending: UserPendingDocument[];
}

const BASE = "/api/admin/legal";

/** Drop empty values so the backend doesn't see `?status=` and treat the empty
 * string as a real filter. */
function cleanParams(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
}

export const legalAdminService = {
  async listDocuments(params: LegalDocumentListParams = {}): Promise<LegalDocument[]> {
    const { data } = await apiClient.get<LegalDocument[]>(`${BASE}/documents`, {
      params: cleanParams(params as Record<string, unknown>),
      headers: adminHeaders(),
    });
    return data;
  },

  async getDocument(id: string): Promise<LegalDocument> {
    const { data } = await apiClient.get<LegalDocument>(`${BASE}/documents/${id}`, { headers: adminHeaders() });
    return data;
  },

  async createDraft(payload: LegalDocumentCreate): Promise<LegalDocument> {
    const { data } = await apiClient.post<LegalDocument>(`${BASE}/documents`, payload, { headers: adminHeaders() });
    return data;
  },

  /** Only valid while the document is still a draft — the backend rejects an
   * edit to a published or archived version. */
  async updateDraft(id: string, payload: LegalDocumentUpdate): Promise<LegalDocument> {
    const { data } = await apiClient.put<LegalDocument>(`${BASE}/documents/${id}`, payload, { headers: adminHeaders() });
    return data;
  },

  /** `effectiveAt` overrides the draft's own effective date at publish time.
   *
   *  `notifyOnly` is a TESTING escape hatch (user ids or emails) — when set,
   *  ONLY those users get emailed/pushed/bell-notified for this publish;
   *  every other active user's `/status` and enforcement are completely
   *  unaffected (they still correctly see this version as pending once it's
   *  published, they just don't get told about it). Omit for a real
   *  publish, where every active user should be notified as normal. */
  async publish(id: string, effectiveAt?: string | null, notifyOnly?: string[]): Promise<LegalDocument> {
    const { data } = await apiClient.post<LegalDocument>(
      `${BASE}/documents/${id}/publish`,
      { effective_at: effectiveAt ?? null, notify_only: notifyOnly?.length ? notifyOnly : null },
      { headers: adminHeaders() },
    );
    return data;
  },

  async archive(id: string): Promise<LegalDocument> {
    const { data } = await apiClient.post<LegalDocument>(`${BASE}/documents/${id}/archive`, {}, { headers: adminHeaders() });
    return data;
  },

  async getAcceptances(id: string, params: LegalAcceptanceListParams = {}): Promise<LegalAcceptanceListResult> {
    const { status = "accepted", acceptedFrom, acceptedTo, ...rest } = params;
    const { data } = await apiClient.get<LegalAcceptanceListResult>(`${BASE}/documents/${id}/acceptances`, {
      params: cleanParams({
        ...rest,
        status,
        accepted_from: acceptedFrom,
        accepted_to: acceptedTo,
      }),
      headers: adminHeaders(),
    });
    return data;
  },

  async getAcceptanceReport(id: string): Promise<LegalAcceptanceReport> {
    const { data } = await apiClient.get<LegalAcceptanceReport>(`${BASE}/documents/${id}/acceptance-report`, {
      headers: adminHeaders(),
    });
    return data;
  },

  /** The fixed set of actions gated on current legal acceptance. */
  async getGatedActions(): Promise<GatedAction[]> {
    const { data } = await apiClient.get<GatedAction[]>(`${BASE}/gated-actions`, { headers: adminHeaders() });
    return data;
  },

  /** `identifier` is a user's Mongo id or their email. 404s with
   * `LEGAL_USER_NOT_FOUND` when neither matches. */
  async getUserAcceptanceHistory(identifier: string): Promise<UserAcceptanceHistory> {
    const { data } = await apiClient.get<UserAcceptanceHistory>(
      `${BASE}/users/${encodeURIComponent(identifier)}/acceptances`,
      { headers: adminHeaders() },
    );
    return data;
  },

  /** Deterministic (no-AI) diff between two HTML blobs — pure function, no
   * document id involved. `oldContent` is the prior version's content (or
   * `null`/omitted for "nothing to compare against yet"), `newContent` is
   * whatever's currently in the editor. See diffing.py for why the returned
   * `changes[]` is a pre-fill, not something auto-published. */
  async diffDocuments(oldContent: string | null | undefined, newContent: string): Promise<LegalDocumentDiff> {
    const { data } = await apiClient.post<LegalDocumentDiff>(
      `${BASE}/documents/diff`,
      { old_content: oldContent ?? null, new_content: newContent },
      { headers: adminHeaders() },
    );
    return data;
  },
};
